import React, { useState, useEffect } from 'react';
import { generatePath, Link as RouterLink, useLocation } from 'react-router-dom';
import { get } from 'lodash';
import { Button, CardContent, Grid, Breadcrumbs, Box, Typography, Tooltip, DialogContentText } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { confirmAlert } from 'react-confirm-alert';
import { BaseCard } from '../../components/Common/BaseCard';
import { GridRow, RowContent, RowTitle } from '../Rescue/RescueEdit/FormGrid';
import { formatAddress, formatDateTime, formatPhoneNumber } from '../../helpers/formatters';
import RegistrationDeclineDialog from './RegistrationDeclineDialog';
import routes from '../../routes';
import RegistrationApproveDialog from './RegistrationApproveDialog';
import { fetchFoodDonor } from '../../actions/foodDonors';
import { approveFoodDonorRequest, declineFoodDonorRequest } from '../../api/foodDonors';
import {
  fetchSiteDonorsLocationsIfNeeded,
  fetchSitePickupLocationsIfNeeded,
  removeSiteTodo,
} from '../../actions/sites';
import { fetchDonorsIfNeeded } from '../../actions/donors';
import useActiveSite from '../../hooks/useActiveSite';
import useNotificationService from '../../hooks/useNotificationService';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import DonorLinkIcon from '../../components/DonorLinkIcon';
import { Colors } from '../../assets/theme/Colors';
import useHasActiveFeatureFlag from '../../hooks/useFeatureFlags';
import { FF_DONOR_LINK } from '../../components/FeatureFlagWrapper';
import RegistrationMergeDialog from './RegistrationMergeDialog';
import { mapFoodDonorToPickupLocationForm } from '../../models/foodDonors';
import ConfirmationDialog from '../../components/ConfirmationDialog';

const FoodDonorRegistrationRequest = ({ history, match, isMobileView }) => {
  const { params } = match;
  const foodDonorId = params.foodDonorId;
  const dispatch = useDispatch();
  const activeSite = useActiveSite();
  const location = useLocation();
  const umbrellaFoodDonors = useSelector(state => state.entities.donors);
  const umbrellaFoodDonorsList = umbrellaFoodDonors.allIds.map(
    umbrellaFoodDonorId => umbrellaFoodDonors.byId[umbrellaFoodDonorId]
  );
  const foodDonors = useSelector(state => state.entities.foodDonors.byId);
  const foodDonor = foodDonors[foodDonorId];
  const siteFoodDonors = useSelector((state) => state.entities.sites.donorsLocations.bySiteId[activeSite.id]);
  const siteFoodDonorsWithoutCurrentDL = (siteFoodDonors || []).filter((donor) => donor.id !== Number(foodDonorId));
  const sitePickupLocations = useSelector((state) => state.entities.sites.pickupLocations);
  const { addErrorNotification, addNotification } = useNotificationService();
  const [showDialog, setShowDialog] = useState(undefined);
  const hasDonorLinkFF = useHasActiveFeatureFlag(FF_DONOR_LINK);
  const todoItem = location?.state?.todoItem;

  useEffect(() => {
    dispatch(fetchSiteDonorsLocationsIfNeeded(activeSite.id));
    dispatch(fetchFoodDonor(foodDonorId));
    dispatch(fetchDonorsIfNeeded(foodDonorId));
    dispatch(fetchSitePickupLocationsIfNeeded(activeSite.id));
  }, [dispatch]);

  const goToDashboard = () => {
    const redirectUrl = isMobileView
      ? generatePath(routes.pendingFoodDonorsRegistrations)
      : generatePath(routes.dashboardSC);
    history.push(redirectUrl);
  };

  // RES-4745 — if this donor already joined a site (e.g. another overlapping site approved the
  // fanned-out request first), a stale email link should bounce back to the dashboard with a notice.
  useEffect(() => {
    if (foodDonor && foodDonor.joined) {
      addNotification('This user has already joined a site.', 'warning');
      goToDashboard();
    }
  }, [foodDonor && foodDonor.joined]);

  const handleDecline = async reason => {
    try {
      const response = await declineFoodDonorRequest(foodDonorId, reason, activeSite.id);
      const json = await response.json();

      if (json && json.data && json.data.already_joined) {
        addNotification('This user has already joined a site.', 'warning');
      }

      // Remove todo item if accessed through todo card review
      if (todoItem?.entityId && todoItem?.entityType && todoItem?.todoType) {
        dispatch(removeSiteTodo(activeSite.id, todoItem.entityId, todoItem.entityType, todoItem.todoType, true));
      }

      goToDashboard();
    } catch (error) {
      setShowDialog(false);
      addErrorNotification(error, 'Unable to decline food donor');
    }
  };

  const handleApprove = async values => {
    const other = values.other;
    const fd = values.food_donor;

    try {
      const response = await approveFoodDonorRequest(foodDonorId, {
        parentFoodDonorId: fd.id !== -1 ? fd.id : null,
        other: other,
        siteId: activeSite.id,
      });
      const json = await response.json();

      if (json && json.data && json.data.already_joined) {
        addNotification('This user has already joined a site.', 'warning');
      }

      goToDashboard();
    } catch (error) {
      setShowDialog(false);
      addErrorNotification(error, 'Unable to approve food donor');
    }
  };

  const handleMerge = (values) => {
    const selectedFoodDonor = values.food_donor;
    const selectedPickupLocation = values.pickup_location;
    const donorLinkExternalId = foodDonor?.donor_link_external_id;

    if (selectedPickupLocation.id < 0) {
      const pickupLocationData = mapFoodDonorToPickupLocationForm(foodDonor);

      return history.push(generatePath(routes.foodDonorPickupLocationCreate, { foodDonorId: selectedFoodDonor.id }), {
        donorLinkMerge: true,
        donorLinkExternalId,
        pickupLocation: pickupLocationData,
        todoItem: location?.state?.todoItem,
        isDonorLinkRegistration: true,
      });
    }
    return history.push(
      generatePath(routes.donor_link_with_pickup_donation_add, {
        location_id: selectedFoodDonor.id,
        pickup_location_id: selectedPickupLocation.id,
      }),
      {
        donor_link_external_id: donorLinkExternalId,
        todoItem: location?.state?.todoItem,
        isDonorLinkRegistration: true,
      }
    );
  };
  const handleDonorLinkRegistration = () =>
    confirmAlert({
      title: 'Ready To Continue?',
      message: 'To approve this donor, you should have the following information:',
      buttons: [
        {
          label: 'Cancel',
        },
        {
          label: "I'm ready",
          color: 'primary',
          onClick: () =>
            history.push(generatePath(routes.foodDonor, { foodDonorId, tab: 'profile' }), {
              todoItem: location?.state?.todoItem,
              isDonorLinkRegistration: true,
            }),
        },
      ],
      customUI: ({ title, message, onClose, buttons }) => (
        <ConfirmationDialog
          title={title}
          buttons={buttons}
          closeDialog={onClose}
          message={message}
          customMessage={
            <ul>
              <li>
                <DialogContentText>Confirmed primary contact</DialogContentText>
              </li>
              <li>
                <DialogContentText>Type and amount of food in a typical pickup</DialogContentText>
              </li>
              <li>
                <DialogContentText>Pickup days and times</DialogContentText>
              </li>
            </ul>
          }
        />
      ),
    });

  if (!foodDonor) {
    return 'loading...';
  }

  return (
    <>
      {isMobileView && (
        <Breadcrumbs aria-label="breadcrumb">
          <StyledRescuerBreadcrumb
            component={RouterLink}
            to={generatePath(routes.mobileDashboard)}
            label="Home"
            icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
          />
          <StyledRescuerTextBreadcrumb
            component={RouterLink}
            to={routes.pendingFoodDonorsRegistrations}
            label="Pending Food Donors"
          />
          <StyledRescuerTextBreadcrumb component="a" href="#" label="Verify" />
        </Breadcrumbs>
      )}
      <Grid container>
        <Grid item container md={12} lg={6} spacing={3}>
          <Grid item xs={12}>
            <BaseCard sx={{ position: 'relative' }}>
              <CardContent>
                <Grid container direction="column" justifyContent="flex-start" alignItems="stretch" spacing={2}>
                  <GridRow>
                    <RowTitle>Registration Date:</RowTitle>
                    <RowContent>{formatDateTime(foodDonor.created_at.value, true)}</RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>Food Donor Location Name:</RowTitle>
                    <RowContent>{foodDonor.name}</RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>Address:</RowTitle>
                    <RowContent>
                      {formatAddress({
                        address: get(foodDonor, 'business_address.address', ''),
                        city: get(foodDonor, 'business_address.city', ''),
                        zip: get(foodDonor, 'business_address.zip_code', ''),
                        st: get(foodDonor, 'business_address.state', ''),
                      })}
                    </RowContent>
                  </GridRow>

                  {foodDonor.store_id && (
                    <GridRow>
                      <RowTitle>Store # or ID:</RowTitle>
                      <RowContent>{foodDonor.store_id}</RowContent>
                    </GridRow>
                  )}

                  <GridRow>
                    <RowTitle>Name:</RowTitle>
                    <RowContent>
                      {get(foodDonor, 'business_address.user.firstname', '') &&
                      get(foodDonor, 'business_address.user.lastname', '')
                        ? `${get(foodDonor, 'business_address.user.firstname', '')} ${get(
                            foodDonor,
                            'business_address.user.lastname',
                            ''
                          )}`
                        : 'N/A'}
                    </RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>Title:</RowTitle>
                    <RowContent>{foodDonor.title || 'N/A'}</RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>E-mail:</RowTitle>

                    <RowContent>
                      <a href={`mailto:${get(foodDonor, 'business_address.user.email', '')}`}>
                        {get(foodDonor, 'business_address.user.email', '')}
                      </a>
                    </RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>Contact Phone#:</RowTitle>

                    {get(foodDonor, 'business_address.user') && (
                      <RowContent>
                        <span>Phone: </span>
                        <a href={`tel:${get(foodDonor, 'business_address.user.phone', '')}`}>
                          {formatPhoneNumber(get(foodDonor, 'business_address.user.phone', ''))}
                        </a>
                        {get(foodDonor, 'business_address.user.phone_ext', '') && (
                          <span>{` (ext: ${get(foodDonor, 'business_address.user.phone_ext', '')})`}</span>
                        )}
                      </RowContent>
                    )}
                  </GridRow>
                </Grid>
              </CardContent>
              {hasDonorLinkFF && foodDonor.donor_link && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <DonorLinkIcon size={36} />
                  <Typography variant="subtitle1" sx={{ color: Colors.donorLink.color }}>
                    Donor Link
                  </Typography>
                </Box>
              )}
            </BaseCard>
          </Grid>

          <Grid item container xs={12} spacing={3}>
            {!!foodDonor?.donor_link ? (
              <Grid item>
                <Button type="button" variant="contained" color="primary" onClick={() => handleDonorLinkRegistration()}>
                  Continue
                </Button>
              </Grid>
            ) : (
              <Grid item>
                <Button type="button" variant="contained" color="primary" onClick={() => setShowDialog('approve')}>
                  Approve
                </Button>
              </Grid>
            )}

            <Grid item>
              <Button type="button" variant="contained" color="primary" onClick={() => setShowDialog('decline')}>
                Decline
              </Button>
            </Grid>

            {!!foodDonor?.donor_link && (
              <Grid item>
                <Tooltip
                  title="Use Merge if this Donor Link request was made by a food donor who has already 
                  registered in your site."
                >
                  <Button type="button" variant="contained" color="secondary" onClick={() => setShowDialog('merge')}>
                    Merge
                  </Button>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <RegistrationDeclineDialog
        isOpen={showDialog === 'decline'}
        handleCancel={() => setShowDialog(undefined)}
        onClose={() => setShowDialog(undefined)}
        handleDecline={handleDecline}
      />
      <RegistrationApproveDialog
        foodDonor={foodDonor}
        isOpen={showDialog === 'approve'}
        handleCancel={() => setShowDialog(undefined)}
        onClose={() => setShowDialog(undefined)}
        handleSave={handleApprove}
        umbrellaFoodDonors={umbrellaFoodDonorsList}
      />
      <RegistrationMergeDialog
        isOpen={showDialog === 'merge'}
        handleCancel={() => setShowDialog(undefined)}
        onClose={() => setShowDialog(undefined)}
        handleSave={handleMerge}
        foodDonors={siteFoodDonorsWithoutCurrentDL}
        pickupLocations={sitePickupLocations}
      />
    </>
  );
};

export default FoodDonorRegistrationRequest;
